import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http, formatMoney } from "@/lib/api";
import { useVenueMarket } from "@/hooks/useVenueMarket";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DASHBOARD } from "@/constants/testIds";
import { Plus, ExternalLink, Pencil, FileDown, Loader2 } from "lucide-react";

export default function EventsList() {
  const [events, setEvents] = useState([]);
  const [venueSlug, setVenueSlug] = useState("");
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const { currency } = useVenueMarket();
  const dateLocale = currency === "USD" ? "en-US" : "en-GB";

  useEffect(() => {
    (async () => {
      try {
        const [e, v] = await Promise.all([http.get("/events"), http.get("/venues/me")]);
        setEvents(e.data);
        setVenueSlug(v.data.slug);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const downloadAttendees = async (event) => {
    setDownloadingId(event.id);
    try {
      const res = await http.get(`/events/${event.id}/attendees.pdf`, { responseType: "blob" });
      // Extract filename from Content-Disposition (falls back to a sensible default)
      let filename = `attendees-${event.id}.pdf`;
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      if (m?.[1]) filename = m[1];
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Attendee sheet downloaded");
    } catch (err) {
      console.error(err);
      toast.error("Couldn't download attendee sheet — please try again");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-xs text-zinc-500 uppercase tracking-widest font-medium">Events</div>
          <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl tracking-tight">All events</h1>
        </div>
        <Link to="/dashboard/events/new">
          <Button className="rounded-full bg-zinc-900 hover:bg-zinc-800" data-testid={DASHBOARD.createEventBtn}>
            <Plus className="w-4 h-4 mr-2" /> New event
          </Button>
        </Link>
      </div>

      <div className="mt-8 rounded-xl bg-white border border-zinc-200 overflow-hidden">
        {loading && <div className="p-8 text-zinc-500 text-sm">Loading…</div>}
        {!loading && events.length === 0 && (
          <div className="p-12 text-center">
            <div className="text-zinc-500 mb-4">No events yet.</div>
            <Link to="/dashboard/events/new">
              <Button className="rounded-full">Create your first event</Button>
            </Link>
          </div>
        )}
        {!loading && events.length > 0 && (
          <table className="w-full">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-zinc-500 font-medium">Title</th>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-zinc-500 font-medium">Date</th>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-zinc-500 font-medium">Tiers</th>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-zinc-500 font-medium">Sold</th>
                <th className="text-left p-4 text-xs uppercase tracking-widest text-zinc-500 font-medium">Status</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {events.map((e) => {
                const totalQty = e.tiers.reduce((a, t) => a + (t.quantity || 0), 0);
                const totalSold = e.tiers.reduce((a, t) => a + (t.sold || 0), 0);
                const isChild = Boolean(e.parent_event_id);
                return (
                  <tr key={e.id} data-testid={DASHBOARD.eventRow(e.id)} className={isChild ? "bg-zinc-50/50" : ""}>
                    <td className="p-4">
                      <div className="font-semibold flex items-center gap-2">
                        {isChild && <span className="text-zinc-400 text-xs">↳</span>}
                        {e.title}
                        {e.series_index !== null && e.series_index !== undefined && (
                          <span className="text-xs text-zinc-400 font-mono">#{e.series_index + 1}</span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500">{e.venue_location}</div>
                    </td>
                    <td className="p-4 text-sm">
                      {new Date(e.start_at).toLocaleString(dateLocale, { dateStyle: "medium", timeStyle: "short" })}
                      {e.is_recurring && !isChild && <div className="text-xs text-indigo-600 mt-0.5">Series · {e.recurrence_rule}</div>}
                    </td>
                    <td className="p-4 text-sm">
                      {e.tiers.map((t) => (
                        <div key={t.id}>{t.name} — {formatMoney(t.price_pence, currency)}</div>
                      ))}
                    </td>
                    <td className="p-4 text-sm">{totalSold} / {totalQty}</td>
                    <td className="p-4">
                      <Badge variant={e.status === "published" ? "default" : "secondary"} className={e.status === "published" ? "bg-emerald-600" : ""}>
                        {e.status}
                      </Badge>
                    </td>
                    <td className="p-4 text-right whitespace-nowrap">
                      {e.status === "published" && venueSlug && (
                        <a href={`/v/${venueSlug}/e/${e.id}`} target="_blank" rel="noreferrer" data-testid={DASHBOARD.eventPublicLink(e.id)}>
                          <Button variant="ghost" size="sm" className="mr-2">
                            <ExternalLink className="w-3.5 h-3.5 mr-1" /> View
                          </Button>
                        </a>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mr-2"
                        disabled={downloadingId === e.id}
                        onClick={() => downloadAttendees(e)}
                        data-testid={`event-attendees-pdf-${e.id}`}
                        title="Download attendee sheet (PDF)"
                      >
                        {downloadingId === e.id ? (
                          <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Preparing…</>
                        ) : (
                          <><FileDown className="w-3.5 h-3.5 mr-1" /> Attendees</>
                        )}
                      </Button>
                      <Link to={`/dashboard/events/${e.id}/edit`} data-testid={DASHBOARD.eventEditBtn(e.id)}>
                        <Button size="sm" className="rounded-full bg-zinc-900 hover:bg-zinc-800">
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Manage
                        </Button>
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
